import { ReactNode, createContext } from 'react'

import { Icon, TextButton, Tooltip } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { OrderStatus } from '__generated__/atlas-types'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import {
  isAccountTypeBrand,
  isEditApprovedShippedRestricted,
} from 'store/currentUser/selectors'

import { OrderData } from '../Overview/Overview'
import withModal from 'containers/ModalHandler/ModalHandler.component'
import AddProductsModalContainer from 'features/Orders/Review/modals/AddProductsModal/AddProductsModal.container'
import { useRedirectToCatalog } from 'features/Orders/Review/review.hooks'
import { useOrderId } from 'features/Orders/orders.hooks'

const BtnWrapper = styled.div`
  display: flex;
  gap: 4px;
`

const StyledTooltip = styled(Tooltip)`
  div:hover {
    text-decoration: none !important;
  }
`

export const AddProductsButtonTestId = 'add-products-toggle'

type AddProductsButtonType = {
  order?: {
    collections: OrderData['collections']['edges']
    brand: OrderData['brand']
    priceType: OrderData['priceType']
    status: OrderData['status']
  }
  openDynamicModal: (value?: unknown) => void
  closeDynamicModal: () => void
  onCloseModal?: () => void
  children?: ReactNode
  id?: string
  isLink?: boolean
  reasons?: string[]
  isDisabled?: boolean
}

const AddProductsContext = createContext({
  handleAddProducts: () => {},
})

const AddProductsLink = ({
  id,
  order,
  openDynamicModal,
  closeDynamicModal,
  onCloseModal,
  children,
  reasons,
  isDisabled,
}: AddProductsButtonType) => {
  const isBrandAccount = useSelector(isAccountTypeBrand)
  const isRestrictEditApprovedShipped = useSelector(
    isEditApprovedShippedRestricted,
  )
  const orderId = useOrderId()
  const redirectToCatalog = useRedirectToCatalog()
  const { trackEvent } = useUserAnalytics()

  const openAddProductsModal = () => {
    openDynamicModal({
      component: AddProductsModalContainer,
      orderId,
      brandId: order?.brand.id,
      priceTypeId: order?.priceType?.id,
      onClose: () => {
        closeDynamicModal()
        onCloseModal?.()
      },
    })
  }

  const handleAddProducts = () => {
    switch (order?.status) {
      case OrderStatus.APPROVED:
      case OrderStatus.SHIPPED:
        if (isBrandAccount && !isRestrictEditApprovedShipped) {
          openAddProductsModal()
        }
        break
      case OrderStatus.PENDING:
        openAddProductsModal()
        break
      case OrderStatus.NOTES:
        openAddProductsModal()
        break
      case OrderStatus.IN_PROGRESS:
        if (isBrandAccount) {
          redirectToCatalog()
        } else {
          openAddProductsModal()
        }
        break
    }
    trackEvent('Order_Review_Add_Products_Button')
  }

  if (children) {
    return (
      <AddProductsContext.Provider value={{ handleAddProducts }}>
        {children}
      </AddProductsContext.Provider>
    )
  }

  const formatedReasons = reasons?.map((reason) => <p>{reason}</p>)

  return (
    <StyledTooltip content={isDisabled ? <div>{formatedReasons}</div> : ''}>
      <TextButton
        data-testid={AddProductsButtonTestId}
        onClick={handleAddProducts}
        disabled={isDisabled}
        id={id}
      >
        <BtnWrapper>
          <Icon iconName="add" iconSize="small" variant="ACTIVE" />{' '}
          <span>Add Products</span>
        </BtnWrapper>
      </TextButton>
    </StyledTooltip>
  )
}

export default withModal(AddProductsLink)
